import React from 'react';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { AppBar, Button, Card, CardContent, CardMedia, CircularProgress, Link, Toolbar, Typography } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { NavigatorEntry } from './NavigatorData';
import { LicenseRequestData, LicenseRequestStatus } from './Paperbase';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { DTFormatter } from '../general/datetime-formatter/DTFormatter';
import { DownloadInfo, Platform, ProductsDataSet, findProductData, findServerData, initialiseDownload} from './DownloadProductData';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';


const styles = (theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: 1046,
      margin: 'auto',
      overflow: 'hidden',
    },
    subPaper: {
      margin: 'auto',
      maxWidth: 946,
      marginBottom: 20,
      paddingTop: 15,
      paddingBottom: 15,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    searchBar: {
      borderBottom: '1px solid ' + MyDeltaColors.MERCURY,
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND
    },
    subSearchBar: {
      margin: 'auto',
      maxWidth: 946,
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND,
      color: DeltaXMLColors.CELERY,
      height: 45,
      //marginTop: 25,
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
    },
    contentWrapper: {
      margin: '40px 10px',
    },
    buttonRefresh: {
      float: 'right',
    },
    progress: {
      float: 'right',
      margin: '2px 8px 0px 0px',
      color: MyDeltaColors.WHITE
    },
    detailsCard: {
      display: 'flex',
      flexDirection: 'row',
      height: 110,
      width: 320,
      alignItems: 'left',
      justifyContent: 'left',
    },
    cardRoot: {
      display: 'flex',
      height: 150,
      width: 450,
      margin: 5,
    },
    content: {
      flex: '1 0 auto',
    },
    controls: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    cardImageCover: {
      height: 150,
      width: 130,
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      verticalAlign: 'middle',
    },
    cardRootContent: {
      display: 'flex',
      alignItems: 'left',
      justifyContent: 'left',
      flexDirection: 'row',
      backgroundColor: DeltaXMLColors.CONCRETE,
      position: 'relative',
    },
    downlaodPageCardButtons: {
      bottom: 9,
      height: '35px',
      marginLeft: '2px'
    },
    productDetails: {
      color: DeltaXMLColors.OXFORD_BLUE,
      backgroundColor: 'transparent'
    },
    alertBar: {
      backgroundColor: MyDeltaColors.HAWKES_BLUE,
      borderRadius: 5,
      paddingTop: 5
    },
    gridContainer: {
      margin: 5
    },
    icon: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      marginLeft: 110
    },
     linkStyle: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      marginTop: '10px'
    }
  });

declare global {
  interface Window { ATL_JQ_PAGE_PROPS: any; }
}

window.ATL_JQ_PAGE_PROPS = window.ATL_JQ_PAGE_PROPS || {};

export interface DownloadLegacyProductProps extends WithStyles<typeof styles> {
  handleRefresh: () => void;
  responseState: LicenseRequestData;
  downloadProductData?: ProductsDataSet;
  downloadSections: NavigatorEntry[];
  dateFormat: string;
}

function DownloadLegacyProduct(props: DownloadLegacyProductProps) {
  const { classes, handleRefresh, responseState, downloadProductData, downloadSections, dateFormat } = props;

  let productDownload: DownloadInfo = initialiseDownload;
  let productDownloadDotNet: DownloadInfo = initialiseDownload;

  let serverDownload: DownloadInfo = initialiseDownload;

  let downloadHeaderText = 'My Downloads'
  let msg = 'products data not yet requested';
  let downloadsFetchError = false;
  // error message:
  const { downloads } = responseState;
  if (downloads.status === LicenseRequestStatus.notYetRequested) {
    msg = 'Authenticating...';
  } if (downloads.status === LicenseRequestStatus.errorResponse) {
    downloadsFetchError = true;
    msg = downloads.text ? `${downloads.text}` : 'Error encountered when requesting downloads';
  } else if (downloads.status === LicenseRequestStatus.inProgress) {
    msg = 'Requesting downloads...';
  } else {
    msg = '';
  }

  const downloadsAvailable = downloads.status !== LicenseRequestStatus.noItems;
  msg = msg === '' ? msg : '[' + msg + ']';
  const showProgressBar = downloads.status === LicenseRequestStatus.inProgress;

  const findCurrentProduct = (productId: string) => {
    [productDownload, productDownloadDotNet] = findProductData(productId, downloadProductData!);
  }

  const findServer = (serverid: string) => {
    [serverDownload] = findServerData(serverid, downloadProductData!);
  }

  const getDownloadURL = (productId: string, platform?: Platform) => {
    platform ? findCurrentProduct(productId): findServer(productId);
    window.location.href = platform === Platform.JAVA ? productDownload.download_url : 
                           platform === Platform.DOTNET ? productDownloadDotNet.download_url :
                           serverDownload.download_url;
  }

  return (
    <Paper className={classes.paper}>
      <AppBar className={classes.searchBar} position="static" color="default" elevation={0}>
        <Toolbar>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h4">
                {downloadHeaderText}
              </Typography>
            </Grid>
            {(downloadsAvailable || downloadsFetchError) &&
              <React.Fragment>
                <Grid item xs={5}>
                  <Typography variant="body2" color="textSecondary" style={{ color: "white" }}>
                    {msg}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  {
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.buttonRefresh}
                      onClick={handleRefresh}>
                      Refresh
                    </Button>
                  }
                  {
                    showProgressBar &&
                    <CircularProgress className={classes.progress} variant="indeterminate" size="30px" />
                  }
                </Grid>
              </React.Fragment>}
          </Grid>
        </Toolbar>
      </AppBar>
      <div className={classes.contentWrapper}>
        {downloadSections && downloadSections.length > 0 &&
          <div>
            <AppBar className={classes.subSearchBar} position="static" color="default" elevation={0}>
              <Toolbar>
                <Typography variant="h5">Legacy Products</Typography>
              </Toolbar>
            </AppBar>
            <Card className={classes.subPaper}>
              <Grid container spacing={2} direction="row" alignItems="center" justify="center">
                {downloadSections.map(({ id: productId, link: productRefLink, icon: productIcon, productInfo: productInfo }) => {
                  findCurrentProduct(productId);
                  return (productDownload.download_url.length !== 0 &&
                    <Card key={productId} className={classes.cardRoot}>
                      <div className={classes.cardRootContent}>
                          <div className={classes.cardImageCover}>
                            <CardMedia
                              component="img"
                              image={productIcon.props.src}
                              title={productId}
                            />
                          </div>
                        <div>
                          <div className={classes.detailsCard}>
                            <CardContent className={classes.content}>
                              <Typography variant="h6" style={{ color: DeltaXMLColors.OXFORD_BLUE }}>
                                <b>{productId}</b>
                              </Typography>
                              <Typography variant="body2" style={{ paddingTop: '10px' }} className={classes.productDetails}>
                                <b>Release:</b>&nbsp;{productDownload.version}
                              </Typography>
                              <Typography variant="body2" className={classes.productDetails}>
                                <b>Date:</b>&nbsp;{DTFormatter.getFormattedDate(productDownload.release_date, dateFormat)}
                              </Typography>
                            </CardContent>
                          </div>
                          <div className={classes.controls}>
                            <Button variant="contained" color="primary" size="small" onClick={() => getDownloadURL(productId, Platform.JAVA)} className={classes.downlaodPageCardButtons}>
                              <GetAppIcon />&nbsp;Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </Grid>
            </Card>
            <div className={classes.subPaper}>
              <AppBar className={classes.alertBar} position="static" elevation={0}>
                <Typography>
                  <Grid container className={classes.gridContainer}>
                    <Grid item xs={2}>
                      <InfoOutlinedIcon fontSize="default" className={classes.icon} />
                    </Grid>
                    <Grid item xs={10}>
                      <Typography align="left" color="primary" variant="body1" >
                        To access older versions of the legacy products, please contact us at <Link className={classes.linkStyle} href="mailto:support@deltaxignia.com">support@deltaxignia.com</Link>
                      </Typography>
                    </Grid>
                  </Grid>
                </Typography>
              </AppBar>
            </div>
          </div>
        }
      </div>
    </Paper>);
}

export default withStyles(styles)(DownloadLegacyProduct);
